import { HttpErrorResponse } from '@angular/common/http';
import { Component, DestroyRef, EventEmitter, inject, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { isAddress } from 'viem';
import { environment } from 'src/environments/environment';
import { canAccessVerifiedUserApis, canWithdrawFunds } from '../../Helper/common-utils';
import { CopyDirective } from '../../directives/copy.directive';
import { CommonService } from '../../services/common.service';
import { LoggerService } from '../../services/logger.service';
import { ProfileMenuService } from '../../services/profile-menu.service';
import { SocketService } from '../../services/socket.service';
import { WebStorageService } from '../../services/web-storage.service';
import { NgIf } from '@angular/common';
import { MaxDecimalsDirective } from '../../../core/directives/max-decimal.directive';
import { WalletLabelPipe } from '../../pipes/wallet-label.pipe';
import { UserStateService } from '../../services/user-state.service';

type UsdcModalTab = 'add' | 'withdraw';

@Component({
    selector: 'app-transak-modal',
    templateUrl: './transak-modal.component.html',
    styleUrls: ['./transak-modal.component.css'],
    imports: [NgIf, FormsModule, ReactiveFormsModule, MaxDecimalsDirective, WalletLabelPipe]
})
export class TransakModalComponent implements OnInit, OnChanges {
  @Input() transakModal: boolean = true;
  @Input() initialTab: UsdcModalTab = 'add';
  @Input() currencies: any[] = [];
  @Input() user: any;
  @Output() closeModal = new EventEmitter<void>();
  @Output() openModal = new EventEmitter<void>();
  @Output() userData = new EventEmitter<any>();
  /** Emitted after a withdrawal is accepted, so the header can re-read wallet balances. */
  @Output() fundsChanged = new EventEmitter<void>();

  activeTab: UsdcModalTab = 'add';
  regulated: boolean = true;
  // buttonStatus: boolean = false; // "Buy now" (Transak) option removed from Add fund tab; kept commented for reference.
  isAddFundsLoading: boolean = false;
  isProcessing: boolean = false;
  withdrawForm!: FormGroup;
  vettedWalletAddressForm!: FormGroup;
  currenciesData: any;
  account: any;
  usdcBalance: number = 0;
  withdrawlWalletAddress: string = '';
  requestedWithdrawalAddress: string = '';
  withdrawalStatus: number | null = null;
  isEditingWithdrawalAddress: boolean = false;
  readonly withdrawStatus = { PENDING: 0, APPROVED: 1, REJECTED: 2 } as const;
  isMaxWithdrawalSelected: boolean = false;
  isWithdrawAmountFocused: boolean = false;

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private webStorageService: WebStorageService,
    public commonService: CommonService,
    private copyDirective: CopyDirective,
    private logger: LoggerService,
    private fb: FormBuilder,
    private toastrService: ToastrService,
    private socketService: SocketService,
    private profileMenuService: ProfileMenuService,
    private userState: UserStateService
  ) { }

  ngOnInit() {
    this.regulated = JSON.parse(this.webStorageService.getLocalStorage('regulated') || 'true');
    this.activeTab = this.initialTab;
    this.syncUserData();

    this.account = this.webStorageService.getLocalStorage('account') != null
      ? JSON.parse(this.webStorageService.getLocalStorage('account') || '')
      : this.account;

    this.withdrawForm = this.fb.group({
      amount: ['', [
        Validators.required,
        Validators.min(0.01),
        this.maxWithdrawalValidator(() => this.maxAllowed)
      ]]
    });

    this.withdrawForm.get('amount')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        this.syncMaxWithdrawalState(value);
      });

    this.vettedWalletAddressForm = this.fb.group({
      walletaddress: ['', [
        Validators.required,
        this.walletAddressValidator(),
        this.sameAsApprovedAddressValidator(() => this.withdrawlWalletAddress, () => this.hasApprovedWithdrawalAddress),
        this.sameAsRegisteredWalletValidator(() => this.user?.wallet_address)
      ]]
    });

    this.syncWithdrawalState();

    const syncedFromHeaderCurrencies = this.syncUsdcBalanceFromCurrencies();
    if (!syncedFromHeaderCurrencies && this.account && canAccessVerifiedUserApis(this.user, JSON.parse(this.webStorageService.getLocalStorage('regulated') || 'true'))) {
      this.getCurrencies();
    }

    this.socketService.withdrawalStatus$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        // Ignore the BehaviorSubject's initial null so the state synced from the user
        // object above is preserved until a real approve/reject event arrives.
        if (!value?.data) return;
        this.applyWithdrawalStatusUpdate(value.data.approval_status, value.data.withdrawal_wallet_address || '');
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['initialTab']) {
      this.activeTab = changes['initialTab'].currentValue || 'add';
    }

    if (changes['user']) {
      this.user = changes['user'].currentValue ?? this.user;
      this.syncUserData();
      this.syncWithdrawalState();
    }

    if (changes['currencies']) {
      this.syncUsdcBalanceFromCurrencies(changes['currencies'].currentValue || []);
    }
  }

  get maxAllowed(): number {
    if (!this.usdcBalance) return 0;
    return this.usdcBalance;
  }

  get balanceDisplayParts(): { whole: string; decimal: string } {
    return this.formatAmountParts(this.usdcBalance);
  }

  get currencyLabel(): string {
    return this.currenciesData?.display_symbol || this.currenciesData?.symbol || 'USDC';
  }

  get availableBalanceDisplayParts(): { whole: string; decimal: string } {
    return this.formatAmountParts(this.usdcBalance);
  }

  get maxAllowedDisplayParts(): { whole: string; decimal: string } {
    return this.formatAmountParts(this.maxAllowed);
  }

  get withdrawAmountDisplayParts(): { whole: string; decimal: string } | null {
    const rawValue = this.withdrawForm?.get('amount')?.value;
    if (rawValue === null || rawValue === undefined || rawValue === '') {
      return null;
    }

    return this.formatAmountParts(rawValue);
  }

  /** True when withdrawals are eligible: regulated user with a provisioned Fireblocks vault. */
  get canWithdraw(): boolean {
    return canWithdrawFunds(this.user, this.regulated);
  }

  /** True once the user has a vetted, admin-approved withdrawal address. */
  get hasApprovedWithdrawalAddress(): boolean {
    return !!this.withdrawlWalletAddress;
  }

  /** True when a submitted address is still awaiting admin approval (and none is approved yet). */
  get hasPendingWithdrawalRequest(): boolean {
    return !this.withdrawlWalletAddress
      && !!this.requestedWithdrawalAddress
      && this.withdrawalStatus === this.withdrawStatus.PENDING;
  }

  /** True while the edit form's entered address matches the already-approved address. */
  get isSameAsApprovedAddressError(): boolean {
    return !!this.vettedWalletAddressForm?.get('walletaddress')?.errors?.['sameAsApproved'];
  }

  /** True while the entered address matches the user's own registered wallet address. */
  get isSameAsRegisteredWalletError(): boolean {
    return !!this.vettedWalletAddressForm?.get('walletaddress')?.errors?.['sameAsRegisteredWallet'];
  }

  /** True when the most recently submitted withdrawal address was rejected by an admin. */
  get hasRejectedWithdrawalRequest(): boolean {
    return !this.hasApprovedWithdrawalAddress && this.withdrawalStatus === this.withdrawStatus.REJECTED;
  }

  getTruncatedMiddleValue(value: string | null | undefined, startLength: number = 14, endLength: number = 6): string {
    if (!value) {
      return '';
    }

    if (value.length <= startLength + endLength + 3) {
      return value;
    }

    return `${value.slice(0, startLength)}...${value.slice(-endLength)}`;
  }

  selectTab(tab: UsdcModalTab): void {
    this.activeTab = tab;
  }

  /**
   * Add Funds card click: calls the Transak widget API (fails when KYB is not approved).
   * On success the Add Funds panel is shown; on failure a fixed error toast is shown.
   */
  selectAddFundsTab(): void {
    if (this.isAddFundsLoading) return;
    this.isAddFundsLoading = true;
    this.commonService.getTransackLink().subscribe({
      next: () => {
        this.isAddFundsLoading = false;
        this.selectTab('add');
      },
      error: (err: HttpErrorResponse) => {
        this.logger.error('Failed to fetch Transak widget URL', err);
        this.isAddFundsLoading = false;
        this.toastrService.error('Add funds is currently unavailable. Please try again later or contact support.');
      },
    });
  }

  /* "Buy now" (Transak) option removed from Add fund tab; kept commented for reference.
  getTransackLink() {
    this.buttonStatus = true;
    this.commonService.getTransackLink().subscribe({
      next: (value: any) => {
        this.logger.debug('Received Transak widget URL response');
        const url = value.data?.widgetUrl;
        if (url) {
          window.open(url, '_blank');
        }
        this.buttonStatus = false;
        this.closeUsdcModal();
      },
      error: (err) => {
        this.logger.error('Failed to fetch Transak widget URL', err);
        this.buttonStatus = false;
      },
    });
  }
  */

  closeUsdcModal() {
    this.withdrawForm?.reset({ amount: '' });
    this.withdrawForm?.markAsPristine();
    this.withdrawForm?.markAsUntouched();
    this.withdrawForm?.get('amount')?.setErrors(null);
    this.isMaxWithdrawalSelected = false;
    this.isWithdrawAmountFocused = false;
    this.isEditingWithdrawalAddress = false;
    this.closeModal.emit();
  }

  public copy(content: string) {
    this.copyDirective.copy(content);
  }

  getCurrencies() {
    this.commonService.getCurrencies().subscribe({
      next: async (response: any) => {
        const currency = response.data.filter((item: any) => !item.is_deleted);
        const usdcCurrency = this.findUsdcCurrency(currency);
        if (usdcCurrency) {
          this.getTokenPrice(usdcCurrency);
        }
      },
      error: (error) => {
        this.logger.error('Failed to load currencies for USDC modal', error);
      }
    });
  }

  async getTokenPrice(currency: any) {
    if (!this.account?.walletAddress || !currency?.address) {
      return;
    }

    const balance = await this.commonService.getTokenBalance(this.account, currency.address);
    this.updateUsdcBalance(currency, Number(balance?.formatted || 0));
  }

  maxWithdrawalValidator(getMax: () => number) {
    return (control: AbstractControl) => {
      const value = Number(control.value);
      const max = getMax();
      return value && value > max ? { exceedMax: true } : null;
    };
  }

  setMaxWithdrawal() {
    // Floor-truncate to 2 decimals: see WithdrawFundsComponent.setMaxWithdrawal
    // for rationale. Must move in lockstep with syncMaxWithdrawalState below.
    this.withdrawForm.get('amount')?.setValue(this.truncateToTwoDecimals(this.maxAllowed).toString());
    this.isMaxWithdrawalSelected = true;
  }

  onWithdrawAmountFocus() {
    this.isWithdrawAmountFocused = true;
  }

  onWithdrawAmountBlur() {
    this.isWithdrawAmountFocused = false;
  }

  focusWithdrawAmount(input: HTMLInputElement) {
    this.isWithdrawAmountFocused = true;
    setTimeout(() => input.focus());
  }

  confirmWithdrawal() {
    if (!this.canWithdraw) {
      this.toastrService.error('Withdrawals are available for custody users only.');
      this.closeUsdcModal();
      return;
    }

    if (this.withdrawForm.valid) {
      this.isProcessing = true;
      const amount = Number(this.withdrawForm.value.amount);
      const payLoad = {
        amount,
        currency_address: this.currenciesData.address
      };

      this.commonService.withdrawUsdc(payLoad).subscribe({
        next: (res: any) => {
          this.toastrService.success(res.message);
          this.fundsChanged.emit();
          this.isProcessing = false;
          this.withdrawForm.reset();
          this.isMaxWithdrawalSelected = false;
          this.isWithdrawAmountFocused = false;
          this.closeUsdcModal();
        },
        error: (err: HttpErrorResponse) => {
          this.toastrService.error(err.error.message);
          this.isProcessing = false;
          this.withdrawForm.reset();
          this.isMaxWithdrawalSelected = false;
          this.isWithdrawAmountFocused = false;
          this.closeUsdcModal();
        },
      });
    }
  }

  addvettedWalletAddress() {
    const addressControl = this.vettedWalletAddressForm.get('walletaddress');
    addressControl?.updateValueAndValidity({ emitEvent: false });

    if (addressControl?.hasError('sameAsApproved')) {
      addressControl.markAsTouched();
      this.toastrService.error('The current withdrawal wallet is already approved. No changes detected. Please update the wallet address before submitting a change request.');
      return;
    }

    if (addressControl?.hasError('sameAsRegisteredWallet')) {
      addressControl.markAsTouched();
      this.toastrService.error(this.registeredAddressConflictMessage);
      return;
    }

    if (this.vettedWalletAddressForm.invalid) {
      this.vettedWalletAddressForm.markAllAsTouched();
      return;
    }

    const submittedAddress = this.vettedWalletAddressForm.value.walletaddress;

    this.isProcessing = true;
    const payLoad = {
      withdraw_address: submittedAddress
    };

    this.commonService.addWithdrawAddressRequest(payLoad).subscribe({
      next: (res: any) => {
        this.toastrService.success(res.message);
        this.isProcessing = false;
        // Flip to the awaiting-approval view in-place (covers first add, pending edit and
        // approved change). The backend clears any previously approved address, so this new
        // request must be re-approved before withdrawals resume.
        this.applyPendingWithdrawalRequest(submittedAddress);
        this.profileMenuService.requestDashboardBannerRefresh();
      },
      error: (err: HttpErrorResponse) => {
        this.toastrService.error(err.message);
        this.isProcessing = false;
      },
    });
  }

  /** Enter edit mode from the awaiting or approved view, pre-filling the current address. */
  startEditWithdrawalAddress() {
    const current = this.withdrawlWalletAddress || this.requestedWithdrawalAddress || '';
    this.vettedWalletAddressForm.reset({ walletaddress: current });
    this.vettedWalletAddressForm.markAsPristine();
    this.vettedWalletAddressForm.markAsUntouched();
    this.isEditingWithdrawalAddress = true;
  }

  /** Leave edit mode without submitting; the previous state (awaiting/approved) is restored by the view. */
  cancelEditWithdrawalAddress() {
    this.isEditingWithdrawalAddress = false;
    this.vettedWalletAddressForm.reset({ walletaddress: '' });
    this.vettedWalletAddressForm.markAsPristine();
    this.vettedWalletAddressForm.markAsUntouched();
  }

  private applyPendingWithdrawalRequest(address: string) {
    this.withdrawlWalletAddress = '';
    this.requestedWithdrawalAddress = address;
    this.withdrawalStatus = this.withdrawStatus.PENDING;
    this.isEditingWithdrawalAddress = false;
    this.persistWithdrawalState('', address, this.withdrawStatus.PENDING);
  }

  private applyWithdrawalStatusUpdate(status: number, approvedAddress: string) {
    this.isEditingWithdrawalAddress = false;
    if (status === this.withdrawStatus.APPROVED) {
      this.withdrawlWalletAddress = approvedAddress;
      this.requestedWithdrawalAddress = approvedAddress;
      this.withdrawalStatus = this.withdrawStatus.APPROVED;
      this.persistWithdrawalState(approvedAddress, approvedAddress, this.withdrawStatus.APPROVED);
    } else {
      // Rejected (or any non-approved status): drop the approved address and fall back to the add view.
      this.withdrawlWalletAddress = '';
      this.withdrawalStatus = typeof status === 'number' ? status : this.withdrawStatus.REJECTED;
      this.persistWithdrawalState('', this.requestedWithdrawalAddress, this.withdrawalStatus);
    }
  }

  /** Keep the standalone key and cached user object in sync (mirrors header-new.updateUserKey). */
  private persistWithdrawalState(approved: string, requested: string, status: number | null) {
    if (approved) {
      this.webStorageService.setLocalStorage('withdrawal_wallet_address', approved);
    } else {
      this.webStorageService.removeLocalStorageItem('withdrawal_wallet_address');
    }

    if (this.user) {
      this.user.withdrawal_wallet_address = approved;
      this.user.requested_withdrawal_wallet_address = requested;
      this.user.requested_withdrawal_wallet_address_status = status;
    }

    const userStr = this.webStorageService.getLocalStorage('user');
    if (!userStr) return;
    try {
      const cachedUser = JSON.parse(userStr);
      cachedUser.withdrawal_wallet_address = approved;
      cachedUser.requested_withdrawal_wallet_address = requested;
      cachedUser.requested_withdrawal_wallet_address_status = status;
      this.webStorageService.setLocalStorage('user', JSON.stringify(cachedUser));
    } catch {
      // Ignore malformed cached user payloads.
    }
  }

  walletAddressValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value?.trim();
      if (!value) return { required: true };

      if (!isAddress(value)) {
        return { invalidWalletAddress: true };
      }

      return null;
    };
  }

  /** Blocks re-submitting the already-approved address as a "change" request. */
  sameAsApprovedAddressValidator(getApproved: () => string, hasApproved: () => boolean): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!hasApproved() || !control.value) return null;

      return this.normalizeAddress(control.value) === this.normalizeAddress(getApproved())
        ? { sameAsApproved: true }
        : null;
    };
  }

  /** Blocks setting your own registered wallet as the withdrawal destination (sender === receiver). */
  sameAsRegisteredWalletValidator(getRegisteredAddress: () => string | undefined): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const registeredAddress = getRegisteredAddress();
      if (!control.value || !registeredAddress) return null;

      return this.normalizeAddress(control.value) === this.normalizeAddress(registeredAddress)
        ? { sameAsRegisteredWallet: true }
        : null;
    };
  }

  private get registeredAddressConflictMessage(): string {
    const walletLabel = this.userState.regulated ? 'account' : 'wallet';
    return `Withdrawal address cannot be the same as your registered ${walletLabel} address.`;
  }

  private normalizeAddress(address: string | null | undefined): string {
    return (address || '').trim().toLowerCase();
  }

  private syncUserData() {
    if (!this.user) {
      this.user = this.webStorageService.getLocalStorage('user') != null
        ? JSON.parse(this.webStorageService.getLocalStorage('user') || '{}')
        : this.user;
    }
  }

  private syncWithdrawalState() {
    const user = this.user || {};
    this.withdrawlWalletAddress = user.withdrawal_wallet_address
      ? user.withdrawal_wallet_address
      : this.webStorageService.getLocalStorage('withdrawal_wallet_address') || '';
    this.requestedWithdrawalAddress = user.requested_withdrawal_wallet_address || '';
    const status = user.requested_withdrawal_wallet_address_status;
    this.withdrawalStatus = [this.withdrawStatus.PENDING, this.withdrawStatus.APPROVED, this.withdrawStatus.REJECTED].includes(status)
      ? status
      : null;

    // withdrawlWalletAddress above feeds sameAsApprovedAddressValidator via closure; Angular
    // won't rerun it on its own since the control's value didn't change, so force it here to
    // keep the inline error in sync if this fires while the user is mid-edit.
    this.vettedWalletAddressForm?.get('walletaddress')?.updateValueAndValidity({ emitEvent: false });
  }

  private syncUsdcBalanceFromCurrencies(currencies: any[] = this.currencies): boolean {
    const usdcCurrency = this.findUsdcCurrency(currencies);
    if (!usdcCurrency) {
      return false;
    }

    this.updateUsdcBalance(usdcCurrency, Number(usdcCurrency.balance || 0));
    return true;
  }

  private findUsdcCurrency(currencies: any[] = []): any {
    const currencyCheckKey = environment.DEFAULT_CHAIN == '0x89' ? 'symbol' : 'name';
    return currencies.find((item: { [key: string]: any }) =>
      String(item?.[currencyCheckKey] || '').toLowerCase() === 'usdc'
    );
  }

  private updateUsdcBalance(currency: any, balance: number) {
    this.currenciesData = currency;
    this.usdcBalance = balance;
    this.withdrawForm?.get('amount')?.updateValueAndValidity({ emitEvent: false });
    this.syncMaxWithdrawalState(this.withdrawForm?.get('amount')?.value);
  }

  private syncMaxWithdrawalState(value: unknown) {
    const numericValue = Number(value);
    // Must match setMaxWithdrawal's floor truncation, otherwise the Max
    // highlight desyncs the moment the user clicks Max.
    const maxValue = this.truncateToTwoDecimals(this.maxAllowed);
    this.isMaxWithdrawalSelected = !Number.isNaN(numericValue) && numericValue === maxValue && maxValue > 0;
  }

  private formatAmountParts(value: number | string): { whole: string; decimal: string } {
    const normalizedValue = this.truncateToTwoDecimals(Number(value || 0));
    const [whole, decimal] = normalizedValue.toFixed(2).split('.');

    return {
      whole: Number(whole).toLocaleString('en-US'),
      decimal
    };
  }

  private truncateToTwoDecimals(value: number): number {
    return Math.floor(Number(value || 0) * 100) / 100;
  }
}
